<div class="page">
  <header class="header">
    <div><h1>My Team</h1><p class="subtitle">Manage your team, members, and benefits.</p></div>
    @if (team) {
      <button mat-stroked-button (click)="openSettings()"><mat-icon>settings</mat-icon> Team Settings</button>
    }
  </header>

  @if (workspace.state.teams.length > 1) {
    <mat-form-field appearance="outline" class="team-select">
      <mat-label>Switch Team</mat-label>
      <mat-select [value]="$safeNavigationMigration(team?.id)" (selectionChange)="selectTeam($event.value)" [disabled]="switching">
        @for (option of workspace.state.teams; track option) {
          <mat-option [value]="option.id">{{ option.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
  }
  @if (workspace.state.loading || switching) {
    <div class="status" role="status"><mat-spinner [diameter]="28"></mat-spinner> Loading your team…</div>
  }
  @if (hasSelectionError) {
    <div role="alert">
      This team could not be loaded.
      @if (workspace.state.teams.length === 1) {
        <button mat-stroked-button (click)="retryTeamSelection()">Retry</button>
      }
      @if (workspace.state.teams.length > 1) {
        <span>Select an available team and try again.</span>
      }
    </div>
  }
  @if (!team && !switching && !workspace.state.loading && !hasSelectionError) {
    <p>Select a team to manage its members and benefits.</p>
  }

  @if (team && !switching) {
    <mat-card class="summary-card">
      <app-team-summary [name]="team.name" [amount]="team.amount || 0"
        [membersCount]="membersLoading || membersError ? null : members.length"
      [canEdit]="true" (edit)="openSettings()"></app-team-summary>
      <div class="status-labels">
        <span>{{ team.approved ? 'Approved' : 'Awaiting approval' }}</span>
        <span>Reimbursements {{ team.disabled ? 'paused' : 'enabled' }}</span>
        <span>Monthly allowance {{ team.topupDisabled ? 'paused' : 'enabled' }}</span>
      </div>
    </mat-card>
    <section class="dash-grid" aria-label="Team management">
      <mat-card class="dash-card members-card">
        <div class="dash-header"><div class="icon-square accent"><mat-icon>groups</mat-icon></div><div><h2>Members</h2><p>People on this team</p></div></div>
        @if (membersLoading) {
          <div class="status" role="status"><mat-spinner [diameter]="24"></mat-spinner> Loading members…</div>
        }
        @if (membersError) {
          <div role="alert">Members could not be loaded. <button mat-button (click)="loadDetails()">Retry</button></div>
        }
        @if (!membersLoading && !membersError) {
          <ul class="member-list">
            @for (member of members | slice:0:8; track member) {
              <li><span>{{ memberName(member) }}</span><span class="muted">{{ member.role === 'admin' ? 'Admin' : 'Member' }}</span></li>
            }
          </ul>
          @if (!members.length) {
            <p class="empty">No members yet. Add members to get started.</p>
          }
          @if (members.length > 8) {
            <p class="muted">Showing 8 of {{ members.length }} members</p>
          }
        }
        <div class="dash-footer"><button mat-stroked-button (click)="openSettings('agency-members')">Manage Members</button></div>
        <p class="helper">Add or upload members, view removed members, and export your member list.</p>
      </mat-card>
      <mat-card class="dash-card">
        <div class="dash-header"><div class="icon-square blue"><mat-icon>mail</mat-icon></div><div><h2>Invitations</h2><p>Pending, sent, and accepted invitations</p></div></div>
        @if (!membersLoading && !membersError) {
          <div class="metrics">
            <div><strong>{{ pendingInvitesCount }}</strong><span>Not invited</span></div>
            <div><strong>{{ sentInvitesCount }}</strong><span>Sent</span></div>
            <div><strong>{{ acceptedInvitesCount }}</strong><span>Accepted</span></div>
          </div>
        }
        @if (membersLoading) {
          <p role="status">Loading invitation status…</p>
        }
        @if (membersError) {
          <p>Invitation status is unavailable. Retry loading members.</p>
        }
        <div class="dash-footer"><app-send-invites-button [teamId]="team.id"
          [disabled]="membersLoading || membersError || !team.approved"
          [pendingInvitesCount]="pendingInvitesCount" [hasPendingAutomation]="hasPendingAutomation"
        (invitesSent)="loadDetails()"></app-send-invites-button></div>
      </mat-card>
      <mat-card class="dash-card">
        <div class="dash-header"><div class="icon-square green"><mat-icon>favorite_border</mat-icon></div><div><h2>Reimbursements & Allowance</h2><p>Your team's reimbursement categories and monthly benefit</p></div></div>
        <div class="metrics"><div><strong>{{ categories.length }}</strong><span>Categories</span></div><div><strong>{{ team.amount || 0 | currency:'USD':'symbol':'1.0-2' }}</strong><span>Per member / month</span></div></div>
        <p class="category-summary">{{ categories.length ? categories.join(', ') : 'No reimbursement categories selected' }}</p>
        <div class="dash-footer"><button mat-stroked-button (click)="editBenefits()">Edit Benefits & Allowance</button></div>
      </mat-card>
      @if (productsEnabled) {
        <mat-card class="dash-card">
          <div class="dash-header"><div class="icon-square green"><mat-icon>redeem</mat-icon></div><div><h2>Products</h2><p>Products available to members of this team</p></div></div>
          @if (productsError) {
            <p role="alert">Product selections could not be loaded.</p>
          }
          @if (productCount === null && !productsError) {
            <p role="status">Loading product selections…</p>
          }
          @if (productCount !== null) {
            <div class="metrics"><div><strong>{{ productCount }}</strong><span>Selected products</span></div></div>
          }
          <div class="dash-footer"><button mat-stroked-button (click)="openSettings('products')">Manage Products</button></div>
        </mat-card>
      }
      <mat-card class="dash-card">
        <div class="dash-header"><div class="icon-square orange"><mat-icon>celebration</mat-icon></div><div><h2>Rewards</h2><p>Reward your team and review past activity</p></div></div>
        <p>Manage one-time rewards, reward uploads, and scheduled reward activity.</p>
        <div class="dash-footer"><button mat-stroked-button (click)="openSettings()">Manage Rewards</button><button mat-button (click)="openSettings('program')">Rewards History</button></div>
      </mat-card>
      <mat-card class="dash-card">
        <div class="dash-header"><div class="icon-square blue"><mat-icon>account_balance_wallet</mat-icon></div><div><h2>Plan & Funding</h2><p>Team plan, funding, and reimbursement settings</p></div></div>
        @if (team.approved) {
          <app-monthly-topup-schedule [teamId]="team.id" [settingsVersion]="team.amount + ':' + team.topupDisabled + ':' + team.defaultPaymentMethod" [disabled]="switching"></app-monthly-topup-schedule>
        }
        @if (team.plan) {
          <p>Current plan: <strong>{{ team.plan }}</strong></p>
        }
        <div class="dash-footer"><button mat-stroked-button (click)="openSettings()">Team Settings</button><button mat-button [disabled]="!team.approved" (click)="openSettings('plan')">View Plan</button></div>
      </mat-card>
    </section>
  }
</div>
